import React, { FC, useCallback, useMemo } from 'react';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useController } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import classNames from 'classnames';

import { generateSelectStyles } from 'buyside/components/presentation/Fields/selectField';

import { debounce } from 'buyside/util/lodashAlt';

import { useEntitiesByBloombergSymbolLazyQuery } from 'interfaces/graphql-typings';

import fieldStyles from 'buyside/components/presentation/Fields/fieldStyles.scss';

const { entityMentionLimit } = require('buyside/util/constantsFromRails.json');

const MentionedEntitiesField: FC = () => {
  const [handleSearchEntity] = useEntitiesByBloombergSymbolLazyQuery();

  const {
    field: { name, onBlur, onChange, ref, value },
    fieldState: { error },
    formState: { isSubmitting }
  } = useController({ name: 'mentionedEntities' });

  const debouncedLoadEntities = debounce(
    (inputValue, callback) =>
      handleSearchEntity({ variables: { query: inputValue } }).then(response => {
        const entities = response.data?.entitiesByBloombergSymbol;

        if (!entities) return callback([]);

        const options = entities.map(entity => ({
          label: `${entity.primarySymbol} - ${entity.name}`,
          value: entity.id
        }));

        return callback(options);
      }),
    1000
  );

  const debouncedLoadEntitiesCallback = useCallback(
    (inputValue: string, callback: (options: any[]) => void) =>
      debouncedLoadEntities(inputValue, callback),
    [debouncedLoadEntities]
  );

  const selectStyles = useMemo(
    () => generateSelectStyles({ error: Boolean(error), size: 'medium' }),
    [error]
  );

  return (
    <div className={classNames(fieldStyles.field, fieldStyles.medium)}>
      <label data-title>Related Tickers</label>
      <AsyncSelect
        classNamePrefix={name}
        isDisabled={isSubmitting}
        isMulti
        loadOptions={debouncedLoadEntitiesCallback}
        onBlur={onBlur}
        onChange={options => onChange(options)}
        noOptionsMessage={({ inputValue }) =>
          inputValue
            ? `No results for ${inputValue.toUpperCase()}`
            : 'Type in a ticker then select or press enter'
        }
        placeholder="Search and select..."
        ref={ref}
        styles={selectStyles as any}
        value={value}
      />
      {error ? (
        <label data-error>
          <FontAwesomeIcon icon={faExclamationCircle} /> {error.message}
        </label>
      ) : null}
      <small>Tag up to {entityMentionLimit} tickers that are relevant to your report</small>
    </div>
  );
};

export default MentionedEntitiesField;
